import React from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import Icon from './Icon';
import { APP_STORE_ROUTE } from '../constants/urls';

const Features = () => {
  const featureStructuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Lown Mortgage Calculator Features",
      "url": "https://www.lown.app/features",
      "description": "Detailed overview of Lown's free, ad-free mortgage calculator, lender comparison, and savings tools for eco-friendly, walkable homebuyers.",
      "inLanguage": "en-US",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://www.lown.app/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Features",
            "item": "https://www.lown.app/features"
          }
        ]
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Lown Homebuying Feature List",
      "description": "Mortgage calculator, lender comparison, and savings features supporting eco-friendly homes, home office space, walkable neighborhoods, and privacy-first planning.",
      "url": "https://www.lown.app/features",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "item": {
            "@type": "Thing",
            "name": "Mortgage affordability calculator",
            "url": "https://www.lown.app/calculator",
            "keywords": [
              "mortgage calculator",
              "how much house can I afford",
              "best mortgage lenders",
              "mortgage savings tracker"
            ]
          }
        },
        {
          "@type": "ListItem",
          "position": 2,
          "item": {
            "@type": "Thing",
            "name": "Loan comparison tool",
            "url": "https://www.lown.app/loan-comparison",
            "keywords": [
              "compare mortgage loans",
              "houses for sale near me",
              "walkable neighborhoods",
              "refinance calculator"
            ]
          }
        },
        {
          "@type": "ListItem",
          "position": 3,
          "item": {
            "@type": "Thing",
            "name": "Savings goal tracker",
            "url": "https://www.lown.app/savings-goals",
            "keywords": [
              "FHA down payment assistance",
              "USDA loan eligibility",
              "VA loan requirements",
              "budget tracker"
            ]
          }
        },
        {
          "@type": "ListItem",
          "position": 4,
          "item": {
            "@type": "Thing",
            "name": "Eco-friendly home planning",
            "keywords": [
              "eco-friendly homes near me",
              "homes with home office space",
              "walkable neighborhoods"
            ]
          }
        }
      ]
    }
  ];

  return (
    <>
      <SEO 
        title="Mortgage Calculator Features to Compare Lenders & Eco-Friendly Homes"
        description="Explore Lown's free, ad-free mortgage calculator, lender comparison, and savings tools to find the best mortgage lenders, plan eco-friendly homes, and hit down payment goals with on-device privacy."
        keywords="mortgage calculator features, compare mortgage lenders, mortgage savings tracker, refinance calculator, eco-friendly homes near me, homes with home office space, walkable neighborhoods, FHA down payment assistance, USDA loan eligibility, VA loan requirements, budget tracker"
        url="https://www.lown.app/features"
        type="article"
        image="https://www.lown.app/lown_homescreen.png"
        structuredData={featureStructuredData}
      />
      
      <div className="features-page">
        <div className="page-header">
          <h1>Mortgage Calculator & Homebuying Features for Modern Buyers</h1>
          <p className="page-subtitle">Lown provides free, ad-free tools to compare mortgage lenders, plan eco-friendly homes with home office space, and navigate walkable neighborhoods with confidence—without ever selling your data.</p>
        </div>

        <div className="features-overview">
          <div className="feature-category animate-fade-in-up">
            <h2 className="heading-with-icon">
              <span className="heading-icon app-icon" aria-hidden="true">
                <Icon name="home" />
              </span>
              Home Buying Tools
            </h2>
            <div className="features-grid">
              <div className="feature-card primary animate-fade-in-up">
                <div className="feature-icon app-icon">
                  <Icon name="calculator" />
                </div>
                <h3>Affordability Calculator</h3>
                <p>Calculate exactly how much home you can afford based on your income, debt, credit score, and down payment. Get detailed breakdowns of monthly payments and total costs.</p>
                <div className="feature-status">
                  <span className="status-badge available">Available Now</span>
                </div>
                <Link to="/calculator" className="feature-cta">Try Calculator</Link>
              </div>
              
              <div className="feature-card animate-fade-in-up">
                <div className="feature-icon app-icon">
                  <Icon name="clipboard" />
                </div>
                <h3>Pre-Approval Estimator</h3>
                <p>Get an estimate of your potential pre-approval amount before visiting lenders. Understand what lenders look for and how to improve your approval chances.</p>
                <div className="feature-status">
                  <span className="status-badge coming-soon">Coming Soon</span>
                </div>
              </div>
            </div>
          </div>

          <div className="feature-category animate-fade-in-up">
            <h2 className="heading-with-icon">
              <span className="heading-icon app-icon" aria-hidden="true">
                <Icon name="chart" />
              </span>
              Loan Management
            </h2>
            <div className="features-grid">
              <div className="feature-card animate-fade-in-up">
                <div className="feature-icon app-icon">
                  <Icon name="scale" />
                </div>
                <h3>Loan Comparison Tool</h3>
                <p>Compare multiple mortgage offers side by side. Analyze rates, terms, fees, and total costs to find the best loan for your situation.</p>
                <div className="feature-status">
                  <span className="status-badge coming-soon">Coming Soon</span>
                </div>
                <Link to="/loan-comparison" className="feature-cta secondary">Learn More</Link>
              </div>
              
              <div className="feature-card animate-fade-in-up">
                <div className="feature-icon app-icon">
                  <Icon name="chart" />
                </div>
                <h3>Amortization Tracker</h3>
                <p>Track your real mortgage payments and see accurate amortization schedules. Monitor principal vs. interest payments and equity building over time.</p>
                <div className="feature-status">
                  <span className="status-badge coming-soon">Coming Soon</span>
                </div>
              </div>
            </div>
          </div>

          <div className="feature-category animate-fade-in-up">
            <h2 className="heading-with-icon">
              <span className="heading-icon app-icon" aria-hidden="true">
                <Icon name="target" />
              </span>
              Payoff Optimization
            </h2>
            <div className="features-grid">
              <div className="feature-card animate-fade-in-up">
                <div className="feature-icon app-icon">
                  <Icon name="bolt" />
                </div>
                <h3>Extra Payment Simulator</h3>
                <p>Model the impact of additional payments on your loan term and total interest. See how small extra payments can save thousands and years off your mortgage.</p>
                <div className="feature-status">
                  <span className="status-badge coming-soon">Coming Soon</span>
                </div>
              </div>
              
              <div className="feature-card animate-fade-in-up">
                <div className="feature-icon app-icon">
                  <Icon name="target" />
                </div>
                <h3>Mortgage Payoff Calculator</h3>
                <p>Calculate different payoff strategies including bi-weekly payments, lump sum payments, and accelerated payment schedules.</p>
                <div className="feature-status">
                  <span className="status-badge coming-soon">Coming Soon</span>
                </div>
              </div>
            </div>
          </div>

          <div className="feature-category animate-fade-in-up">
            <h2 className="heading-with-icon">
              <span className="heading-icon app-icon" aria-hidden="true">
                <Icon name="trophy" />
              </span>
              Savings & Goals
            </h2>
            <div className="features-grid">
              <div className="feature-card animate-fade-in-up">
                <div className="feature-icon app-icon">
                  <Icon name="gamepad" />
                </div>
                <h3>Savings Goal Tracker</h3>
                <p>Gamify your savings with goal setting, progress tracking, and achievement rewards. Stay motivated on your journey to homeownership or mortgage freedom.</p>
                <div className="feature-status">
                  <span className="status-badge coming-soon">Coming Soon</span>
                </div>
                <Link to="/savings-goals" className="feature-cta secondary">Learn More</Link>
              </div>
              
              <div className="feature-card animate-fade-in-up">
                <div className="feature-icon app-icon">
                  <Icon name="phone" />
                </div>
                <h3>Progress Dashboard</h3>
                <p>Get a comprehensive view of your homeownership journey with personalized insights, progress tracking, and actionable recommendations.</p>
                <div className="feature-status">
                  <span className="status-badge coming-soon">Coming Soon</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="app-benefits animate-fade-in-up">
          <h2>Why Choose Lown?</h2>
          <div className="benefits-grid">
            <div className="benefit-card animate-fade-in-up">
              <div className="benefit-icon app-icon">
                <Icon name="lock" />
              </div>
              <h3>Privacy First</h3>
              <p>Your financial data stays with you. No third-party aggregators or data selling. We believe in transparent, secure financial management.</p>
            </div>
            
            <div className="benefit-card animate-fade-in-up">
              <div className="benefit-icon app-icon">
                <Icon name="home" />
              </div>
              <h3>Built by Homeowners</h3>
              <p>Created by people who understand the homeownership journey firsthand. Every feature is designed with real user experiences in mind.</p>
            </div>
            
            <div className="benefit-card animate-fade-in-up">
              <div className="benefit-icon app-icon">
                <Icon name="sparkle" />
              </div>
              <h3>Modern Experience</h3>
              <p>Beautiful, intuitive tools designed for today's homebuyers. Clean interfaces, smooth interactions, and mobile-first design.</p>
            </div>
            
            <div className="benefit-card animate-fade-in-up">
              <div className="benefit-icon app-icon">
                <Icon name="book" />
              </div>
              <h3>Educational Focus</h3>
              <p>Learn as you go with built-in explanations, tips, and guidance. We believe in empowering users with knowledge, not just tools.</p>
            </div>
          </div>
        </div>

        <div className="getting-started">
          <h2>Get Started Today</h2>
          <div className="getting-started-content">
            <div className="steps">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3>Try Our Free Calculator</h3>
                  <p>Start with our affordability calculator to understand your home buying budget and monthly payment estimates.</p>
                  <Link to="/calculator" className="step-cta">Calculate Affordability</Link>
                </div>
              </div>
              
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3>Download the App</h3>
                  <p>Install Lown on your iPhone or iPad and unlock the full experience with calculators, comparisons, and progress tracking.</p>
                  <Link to={APP_STORE_ROUTE} className="step-cta">Open App Store</Link>
                </div>
              </div>
              
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h3>Track Your Journey</h3>
                  <p>Use Lown to track your progress, compare options, and make informed decisions throughout your homeownership journey.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="testimonial-section">
          <h2>What Users Are Saying</h2>
          <div className="testimonials">
            <div className="testimonial">
              <div className="testimonial-content">
                <p>"Lown's affordability calculator helped me understand exactly what I could afford and gave me the confidence to start my homebuying journey."</p>
                <div className="testimonial-author">
                  <strong>Sarah M.</strong>
                  <span>First-time homebuyer</span>
                </div>
              </div>
            </div>
            
            <div className="testimonial">
              <div className="testimonial-content">
                <p>"The extra payment simulator showed me how I could save $50,000 in interest by making small additional payments each month."</p>
                <div className="testimonial-author">
                  <strong>Mike R.</strong>
                  <span>Current homeowner</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Features;
